<template lang="pug">
el-drawer(
    :visible.sync="localVisible"
    :close-on-press-escape="true"
    :wrapper-closable="true"
    :modal="true"
    :size="drawerSize"
    custom-class="preview-drawer"
    @closed="onClosed"
)
    template(slot="title")
        .preview-header
            span.preview-title {{ title }}
            el-button-group.breakpoint-buttons
                el-tooltip(:content="$t('Mobile')" placement="bottom")
                    el-button(
                        :type="activeBreakpoint === 'mobile' ? 'primary' : 'default'"
                        size="small"
                        icon="el-icon-fa-mobile-alt fas"
                        @click="setBreakpoint('mobile')"
                    )
                el-tooltip(:content="$t('Tablet')" placement="bottom")
                    el-button(
                        :type="activeBreakpoint === 'tablet' ? 'primary' : 'default'"
                        size="small"
                        icon="el-icon-fa-tablet-alt fas"
                        @click="setBreakpoint('tablet')"
                    )
                el-tooltip(:content="$t('Desktop')" placement="bottom")
                    el-button(
                        :type="activeBreakpoint === 'desktop' ? 'primary' : 'default'"
                        size="small"
                        icon="el-icon-fa-desktop fas"
                        @click="setBreakpoint('desktop')"
                    )
    .preview-content(v-loading="loading")
        iframe(
            v-if="src"
            :src="src"
            ref="previewIframe"
            @load="onLoad"
            @error="onError"
        )
    .drawer-footer
        el-button(
            round
            size="mini"
            icon="el-icon-fa-times fas"
            @click="dismiss"
        ) {{ $t('Dismiss') }}
        el-button(
            round
            size="mini"
            type="primary"
            icon="el-icon-fa-sync fas"
            :loading="clearingCache"
            @click="handleClearCache"
        ) {{ $t('Clear cache') }}
</template>

<i18n>
{
    "de": {
        "Preview": "Vorschau",
        "Mobile": "Mobil",
        "Tablet": "Tablet",
        "Desktop": "Desktop",
        "Dismiss": "Schließen",
        "Clear cache": "Cache leeren",
        "Cache cleared successfully": "Cache erfolgreich gelöscht",
        "Cache could not be cleared": "Cache konnte nicht gelöscht werden",
        "Preview could not be rendered": "Vorschau konnte nicht generiert werden"
    }
}
</i18n>

<script>
import { useSetupStore } from '@/stores';

export default {
    name: 'PreviewDrawer',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        src: {
            type: String,
            default: null
        },
        title: {
            type: String,
            default() {
                return this.$t('Preview');
            }
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            activeBreakpoint: 'desktop',
            clearingCache: false
        };
    },
    computed: {
        localVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit('update:visible', value);
            }
        },
        drawerSize() {
            switch (this.activeBreakpoint) {
                case 'mobile':
                    return '420px';
                case 'tablet':
                    return '820px';
                default:
                    return '85%';
            }
        },
        activeCms() {
            return useSetupStore().activeCms;
        }
    },
    methods: {
        setBreakpoint(breakpoint) {
            this.activeBreakpoint = breakpoint;
        },
        onClosed() {
            this.activeBreakpoint = 'desktop';
            this.$emit('closed');
        },
        onLoad() {
            this.$emit('load');
        },
        onError(error) {
            this.$notify.error({
                title: this.$t('An error occurred!'),
                message: this.$t('Preview could not be rendered'),
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            });
            this.$emit('error', error);
        },
        dismiss() {
            this.localVisible = false;
        },
        handleClearCache() {
            this.clearingCache = true;
            useSetupStore().clearCacheForCms(this.activeCms)
                .then(() => {
                    this.$notify.success({
                        title: this.$t('Cache cleared successfully'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                    this.localVisible = false;
                })
                .catch((error) => {
                    console.error(error);
                    this.$notify.error({
                        title: this.$t('Cache could not be cleared'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .finally(() => {
                    this.clearingCache = false;
                });
        }
    }
};
</script>

<style lang="sass">
.el-drawer__wrapper
    .el-drawer.preview-drawer
        transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1)

        .el-drawer__header
            margin-bottom: 0
            padding: 16px 20px
            border-bottom: 1px solid #ebeef5

        .preview-header
            display: flex
            align-items: center
            justify-content: space-between
            width: 100%
            padding-right: 40px

            .preview-title
                font-size: 18px
                font-weight: 600
                color: #303133

            .breakpoint-buttons
                .el-button
                    padding: 8px 10px
                    transition: all 0.2s ease

        .el-drawer__body
            display: flex
            flex-direction: column
            height: calc(100vh - 61px)
            padding: 0
            overflow: hidden

        .preview-content
            flex: 1
            display: flex
            flex-direction: column
            background: #e8e8e8
            overflow: hidden

            iframe
                flex: 1
                border: none
                background: transparent

        .drawer-footer
            display: flex
            justify-content: space-between
            align-items: center
            padding: 12px 20px
            border-top: 1px solid #ebeef5
            background: #fff
            flex-shrink: 0
</style>
